import React, { useEffect, useMemo, useState } from 'react'
import { VStack, Heading, Box } from '@chakra-ui/react'
import type { Glossary } from '@/types/glossary'
import { SectionContainer } from '@/components/SectionContainer'
import { Desktop } from './Desktop'
import { Mobile } from './Mobile'
import { useTranslation } from 'react-i18next'
import { BoxProps } from '@chakra-ui/react'
import alphaLangOrder from '@/configs/alphaLangOrder'

type LetterIndexSectionProps = BoxProps & {
  data: Glossary[]
}

const DESKTOP_MIN_WIDTH = 1024

export const LetterIndexSection = ({
  data,
  ...restProps
}: LetterIndexSectionProps) => {
  const { t, i18n } = useTranslation()
  const letterOrder =
    alphaLangOrder[i18n.language]?.keyOrder ?? alphaLangOrder['en'].keyOrder
  const itemsDict = useMemo(() => {
    let tempDict: { [key: string]: Glossary[] } = {}
    letterOrder.forEach((letter) => {
      tempDict[letter] = []
    })
    data.forEach((word) => {
      let uppercaseWord = word.title.toUpperCase().charAt(0)
      if (letterOrder.indexOf(uppercaseWord) !== -1) {
        tempDict[uppercaseWord].push(word)
      } else {
        tempDict['#'].push(word)
      }
    })
    letterOrder.forEach((letter) =>
      tempDict[letter].sort((a: Glossary, b: Glossary) =>
        a.title.toLowerCase().localeCompare(b.title.toLowerCase())
      )
    )
    return tempDict
  }, [data, letterOrder])

  const [screenWidth, setScreenWidth] = useState(0)
  const [selectedLetter, setSelectedLetter] = useState('')

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth)
    }
    window.addEventListener('resize', handleResize)
    handleResize()
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const availableLetters = useMemo(() => {
    let tempArr: string[] = []
    for (let i = 0; i < letterOrder.length; i++) {
      if (itemsDict[letterOrder[i]].length !== 0) {
        tempArr.push(letterOrder[i])
      }
    }
    return tempArr
  }, [itemsDict, letterOrder])

  useEffect(() => {
    const scrollToTargetAdjusted = (id: string) => {
      const element = document.getElementById(id)
      const headerOffset =
        document.getElementById('letter-container')?.offsetHeight
      if (element && headerOffset) {
        const elementPosition = element.getBoundingClientRect().top
        const offsetPosition =
          elementPosition + window.pageYOffset - headerOffset

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        })
      }
    }
    if (!selectedLetter) return
    scrollToTargetAdjusted(selectedLetter)
  }, [selectedLetter])

  return (
    <SectionContainer containerStyle={restProps}>
      <VStack spacing={8} alignItems="start">
        <Heading variant={'h3'}>{t('homepage.glossaryAToZ')}</Heading>
        {screenWidth >= DESKTOP_MIN_WIDTH ? (
          <Box display={['none', null, null, 'block']} width={'100%'}>
            <Desktop
              availableLetters={availableLetters}
              itemsDict={itemsDict}
              selectedLetter={selectedLetter}
              setSelectedLetter={setSelectedLetter}
            />
          </Box>
        ) : (
          <Box display={['block', null, null, 'none']} width={'100%'}>
            <Mobile
              availableLetters={availableLetters}
              itemsDict={itemsDict}
              selectedLetter={selectedLetter}
              setSelectedLetter={setSelectedLetter}
            />
          </Box>
        )}
      </VStack>
    </SectionContainer>
  )
}
